import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import './TickerBanner.css';

interface TickerQuote {
    symbol: string;
    price: number;
    variation: number;
}

interface TickerData {
    date: string | null;
    quotes: TickerQuote[];
}

const TickerBanner: React.FC = () => {
    const { token } = useAuth();
    const [data, setData] = useState<TickerData>({ date: null, quotes: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token) return;

        const fetchTicker = async () => {
            try {
                const response = await fetch(`${API_URL}/v1/market/ticker`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.ok) {
                    const result = await response.json();
                    setData(result);
                }
            } catch (error) {
                console.error('Error fetching ticker data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTicker();
        // Refresh every 5 minutes
        const interval = setInterval(fetchTicker, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [token]);

    if (loading || data.quotes.length === 0) return null;

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Double the quotes to create a seamless infinite scroll
    const displayQuotes = [...data.quotes, ...data.quotes];

    return (
        <div className="ticker-container">
            <div className="ticker-date">
                {data.date ? `Dernières cotations : ${formatDate(data.date)}` : 'Chargement...'}
            </div>
            <div className="ticker-scroll">
                {displayQuotes.map((quote, index) => (
                    <div key={`${quote.symbol}-${index}`} className="ticker-item">
                        <span className="ticker-symbol">{quote.symbol}</span>
                        <span className="ticker-price">{quote.price.toLocaleString('fr-FR')}</span>
                        <span className={`ticker-variation ${quote.variation > 0 ? 'up' : quote.variation < 0 ? 'down' : 'neutral'}`}>
                            {quote.variation > 0 ? <TrendingUp size={12} /> : quote.variation < 0 ? <TrendingDown size={12} /> : <Minus size={12} />}
                            {/* Le signe est écrit : la couleur et la flèche seules ne suffisent ni aux
                                daltoniens ni aux lecteurs d'écran. */}
                            {quote.variation > 0 ? '+' : quote.variation < 0 ? '−' : ''}{Math.abs(quote.variation).toFixed(2)}%
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TickerBanner;
